import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { useMyNavigation } from "@/hooks/useMyNavigation"
import { setIsRecordsUpdated } from "@/core/app/slices/uassist/formSlice"
import { postMessageData } from "@/core/app/slices/banners/bannerThunkApi"
import { updateOrder } from "@/core/app/slices/order/orderThunkApi"
import { getRefinementArches, getuAssistArches, toError } from "../formUtils"
import { useLocation, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useCallback } from "react"
import { caseManagement } from "@/gluelayer"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { getAttentionBannerButton } from "@/modules/Patient/Overview/banner"
import { FormContentDetails } from "../types"
import { RetainerFasttrackOrderType } from "@/core/model/interface/AlignerOrder"

/**
 * Parameters for handleCaseSubmit in useSubmitUassist.
 */
interface HandleCaseSubmitParams {
  details: FormContentDetails
  data: Record<string, any>
  formTemplateList: any[]
  formName: string
  unlockFunc: () => void
}

/**
 * Custom hook for handling Uassist submission flows, including attention-needed and case submit.
 * Returns two callbacks: handleAttentionNeededSubmit and handleCaseSubmit.
 */
export const useSubmitUassist = () => {
  const dispatch = useAppDispatch()
  const navigate = useMyNavigation()
  const location = useLocation()
  const { t } = useTranslation("common")
  const { caseId, patientId } = useParams<{ caseId: string; patientId: string }>()
  const { bannerData } = useAppSelector((state) => state.bannerService)
  const { onHoldMsg } = useAppSelector((state) => state.formService)
  const { clinicalSettings } = useAppSelector((state) => state.userService)
  const { caseDetail } = useAppSelector((state) => state.caseService)

  /**
   * Handles submission for attention-needed forms, posting messages and navigating on success.
   */
  const handleAttentionNeededSubmit = useCallback(async (isRefinement: boolean): Promise<void> => {
    try {
      await dispatch(
        postMessageData({
          patientId,
          caseId: caseId.toString(),
          payload: {
            messages: [
              {
                message: getAttentionBannerButton(bannerData?.warning_subject)
                  .message,
              },
            ],
          ...(onHoldMsg && { "doctor_message": onHoldMsg }),
          },
        }),
      ).then((res: any) => {
        if (res && res.payload && res.payload.status === "Success") {
          dispatch(setIsRecordsUpdated(false))
            navigate(
              `/overview/patient/${patientId}/case/${caseId}/confirmation`,
            )
          // dispatch(getCaseByPatient({ patientId }))
        }
      })
    } catch (error) {
      throw toError(
        error,
        "Failed to submit attention-needed form to Uassist",
      )
    }
  }, [dispatch, patientId, caseId, bannerData, onHoldMsg, navigate, t])

  /**
   * Handles case submission, updating order, uploading zips, and navigating on success.
   */
  const handleCaseSubmit = useCallback(
    async ({ details, data, formTemplateList, formName, unlockFunc }: HandleCaseSubmitParams): Promise<void> => {
      try {
        const normalizedFormName = (formName || "").toLowerCase()
        const isRefinement = [
          FORM_CONSTANT.REFINEMENT.toLowerCase(),
          FORM_CONSTANT.REFINEMENT_2.toLowerCase(),
        ].includes(normalizedFormName)
        const isRetainer =
          normalizedFormName === FORM_CONSTANT.RETAINER.toLowerCase()
        const { upperStr, lowerStr } = getRefinementArches(
          caseDetail,
          formTemplateList,
          {...details, formName},
          data,
        )
        const { upper, lower, bracket_removal } = getuAssistArches(
          isRefinement,
          isRetainer,
          data,
        )
        const finalRetainer = !!(data.final_retainer?.toLowerCase() === "yes")
        const bracketRemoval = data.bracket_removal?.value?.[0]?.toLowerCase() === "yes"
        const refinementOrder = finalRetainer
          ? bracketRemoval
            ? RetainerFasttrackOrderType.BRACKET_REMOVAL_UASSIST
            : RetainerFasttrackOrderType.RETAINER_UASSIST
          : "Refinement uAssist"
        const result: any = await dispatch(
          updateOrder({
            caseId: caseId,
            type: isRefinement
              ? refinementOrder
              : isRetainer
              ? bracket_removal
                ? "Bracket Removal Uassist"
                : "Retainer Uassist"
              : "uAssist",
            upper: isRefinement ? upperStr : upper,
            lower: isRefinement ? lowerStr : lower,
            ...(finalRetainer && { is_final_retainer: finalRetainer }),
            ...(isRefinement && { is_refinement: true }),
          }),
        )
        if (result.payload && result.payload?.status === "Success") {
          if (location.pathname.includes("/clinical/")) {
            // upload zips
            caseManagement
              .saveJsonDataToZip(
                JSON.stringify(clinicalSettings),
                "uassist_team_settings.json",
                "Setting Data2",
              )
              .then((res: unknown) => {
                if (res) {
                  unlockFunc && unlockFunc()
                  if (isRefinement) {
                    navigate(
                      `/overview/patient/${patientId}/case/${caseId}/refinement/confirmation`,
                    )
                  }
                }
              })
          } else {
            navigate(
              `/records/patient/${patientId}/case/${caseId}/summary/confirmation${location.search}`,
            )
          }
        } else {
          throw new Error(t("uassist.uAssistForm.uassistSubmitFailed"))
        }
      } catch (error) {
        throw toError(error, "Failed to submit case to Uassist")
      }
    },
    [dispatch, patientId, caseId, clinicalSettings, caseDetail, navigate, t],
  )

  return { handleAttentionNeededSubmit, handleCaseSubmit }
}
